import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Ticket } from "lucide-react";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = {
  code: "", name_en: "", name_ar: "", type: "percent", value: 10,
  start_date: "", end_date: "", usage_limit: 0, per_customer_limit: 1,
  min_invoice_amount: 0, applicable_service_ids: [], applicable_product_ids: [],
  active: true, description: "",
};

const TYPES = [
  { id: "percent", labelEn: "Percentage %", labelAr: "نسبة %" },
  { id: "fixed", labelEn: "Fixed Amount", labelAr: "مبلغ ثابت" },
  { id: "first_booking", labelEn: "First Booking", labelAr: "أول حجز" },
  { id: "service", labelEn: "Service Specific", labelAr: "خدمة محددة" },
  { id: "bogo", labelEn: "Buy 1 Get 1", labelAr: "اشتر 1 احصل على 1" },
];

export default function Coupons() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/coupons"), api.get("/services")]);
    setItems(r1.data); setServices(r2.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.code) return toast.error(lang === "ar" ? "الكود مطلوب" : "Code required");
    try {
      if (editId) await api.put(`/coupons/${editId}`, form);
      else await api.post("/coupons", form);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الكوبون؟" : "Delete coupon?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/coupons/${id}`); load();
  };

  const edit = (c) => { setForm({ ...c }); setEditId(c.coupon_id); setOpen(true); };

  const inputCls = "w-full px-3 py-2 border rounded-md bg-[var(--bg)]";

  return (
    <div className="space-y-6" data-testid="coupons-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "الكوبونات والعروض" : "Coupons & Promotions"}</h1>
        <button data-testid="add-coupon-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--gold)] text-white"><Plus size={16} /> {lang === "ar" ? "كوبون جديد" : "New Coupon"}</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.coupon_id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[var(--rose)]/10 flex items-center justify-center text-[var(--rose)]"><Ticket size={20} /></div>
                <div>
                  <div className="font-mono font-bold text-lg text-[var(--gold)]">{c.code}</div>
                  <div className="text-sm text-[var(--text-muted)]">{lang === "ar" ? c.name_ar : c.name_en}</div>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(c)} className="p-1.5 rounded hover:bg-[var(--gold)]/10 text-[var(--gold)]"><Pencil size={14} /></button>
                <button data-testid={`delete-coupon-${c.coupon_id}`} onClick={() => remove(c.coupon_id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-[var(--gold)]/10 text-[var(--gold)]">
                {c.type === "percent" ? `${c.value}%` : c.type === "fixed" ? `${c.value} ${lang === "ar" ? "ر.ق" : "QAR"}` : c.type}
              </span>
              <span className={`px-2 py-1 rounded ${c.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{c.active ? (lang === "ar" ? "نشط" : "Active") : (lang === "ar" ? "غير نشط" : "Inactive")}</span>
              {c.usage_limit > 0 && <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">{c.used_count}/{c.usage_limit}</span>}
            </div>
            {c.end_date && <div className="text-xs text-[var(--text-muted)] mt-2">{lang === "ar" ? "ينتهي:" : "Expires:"} {c.end_date}</div>}
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-3 text-center text-[var(--text-muted)] py-12">{lang === "ar" ? "لا توجد كوبونات" : "No coupons yet"}</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="bg-[var(--surface)] rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-xl font-heading mb-4">{editId ? (lang === "ar" ? "تعديل كوبون" : "Edit Coupon") : (lang === "ar" ? "كوبون جديد" : "New Coupon")}</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <input data-testid="coupon-code" placeholder={lang === "ar" ? "الكود (SAVE10)" : "Code (SAVE10)"} className={`${inputCls} font-mono`} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              <select data-testid="coupon-type" className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                {TYPES.map((t) => <option key={t.id} value={t.id}>{lang === "ar" ? t.labelAr : t.labelEn}</option>)}
              </select>
              <input placeholder={lang === "ar" ? "الاسم (إنجليزي)" : "Name (English)"} className={inputCls} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
              <input placeholder="الاسم AR" className={inputCls} value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
              <input data-testid="coupon-value" type="number" placeholder={lang === "ar" ? "القيمة" : "Value"} className={inputCls} value={form.value} onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value || 0) })} />
              <input type="number" placeholder={lang === "ar" ? "حد أدنى للفاتورة" : "Min invoice amount"} className={inputCls} value={form.min_invoice_amount} onChange={(e) => setForm({ ...form, min_invoice_amount: parseFloat(e.target.value || 0) })} />
              <input type="date" placeholder={lang === "ar" ? "تاريخ البداية" : "Start date"} className={inputCls} value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              <input type="date" placeholder={lang === "ar" ? "تاريخ النهاية" : "End date"} className={inputCls} value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              <input type="number" placeholder={lang === "ar" ? "حد الاستخدام (0=بلا حد)" : "Usage limit (0=unlimited)"} className={inputCls} value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: parseInt(e.target.value || 0) })} />
              <input type="number" placeholder={lang === "ar" ? "حد لكل عميل" : "Per customer limit"} className={inputCls} value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: parseInt(e.target.value || 1) })} />
              <textarea placeholder={lang === "ar" ? "وصف" : "Description"} className={`${inputCls} md:col-span-2`} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <label className="flex items-center gap-2 md:col-span-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {lang === "ar" ? "نشط" : "Active"}</label>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-2 border rounded-md">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button data-testid="save-coupon-btn" onClick={save} className="flex-1 px-4 py-2 bg-[var(--gold)] text-white rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
